// Public Telegram identity is shared by the registry, import and SERP checks.
export const TELEGRAM_HOSTS = ['t.me', 'telegram.me', 'telegram.dog'] as const;
const reserved = new Set([
  's',
  'c',
  'joinchat',
  'share',
  'addstickers',
  'addemoji',
  'proxy',
  'socks',
  'login',
  'confirmphone',
  'iv',
  'setlanguage',
  'addtheme',
  'bg',
  'contact',
  'invoice',
  'boost',
  'giftcode',
  'm',
  'nft',
  'a',
  'b',
  'k',
  'z',
  'www',
  'addlist',
  'addstyle',
  'auction',
  'auth',
  'call',
  'web',
]);
function publicUsername(value: string): string | null {
  const username = value.toLowerCase();
  return /^[a-z][a-z0-9_]{0,31}$/.test(username) && !reserved.has(username) ? username : null;
}
export function telegramSubdomainUsername(hostname: string): string | null {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (!host.endsWith('.t.me')) return null;
  const subdomain = host.slice(0, -'.t.me'.length);
  return subdomain.length >= 2 ? publicUsername(subdomain) : null;
}
export function isTelegramHostname(hostname: string): boolean {
  const host = hostname
    .toLowerCase()
    .replace(/\.$/, '')
    .replace(/^www\./, '');
  return (TELEGRAM_HOSTS as readonly string[]).includes(host) || host.endsWith('.t.me');
}
export function isTelegramUrl(url: URL): boolean {
  return isTelegramHostname(url.hostname);
}
export function telegramPublicLink(url: URL): { username: string; postId: string | null } | null {
  if (
    !['http:', 'https:'].includes(url.protocol) ||
    !isTelegramUrl(url) ||
    url.username ||
    url.password ||
    url.port
  )
    return null;
  const hostname = url.hostname.toLowerCase().replace(/\.$/, '');
  const host = (TELEGRAM_HOSTS as readonly string[]).some((alias) => hostname === `www.${alias}`)
    ? hostname.slice(4)
    : hostname;
  let path = url.pathname;
  if (host.endsWith('.t.me')) {
    const subdomain = telegramSubdomainUsername(host);
    if (!subdomain) return null;
    path = `/${subdomain}${path}`;
  }
  // Only public profiles and numeric posts identify the channel; invites/services do not.
  const match = /^\/(?:s\/)?([a-z][a-z0-9_]{0,31})(?:\/(\d+))?\/?$/i.exec(path);
  const username = match?.[1] ? publicUsername(match[1]) : null;
  return username ? { username, postId: match?.[2] ?? null } : null;
}
export function telegramUsername(input: string): string | null {
  const value = input.trim();
  if (/^@?[a-z][a-z0-9_]{0,31}$/i.test(value)) return publicUsername(value.replace(/^@/, ''));
  let url: URL;
  try {
    url = new URL(/^(?:https?:)?\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
  return telegramPublicLink(url)?.username ?? null;
}

// A directory card describes a channel; it is never an alias of its owned URL.
export function tgstatChannel(input: string): string | null {
  const url = new URL(input);
  if (
    !['http:', 'https:'].includes(url.protocol) ||
    url.username ||
    url.password ||
    url.port ||
    !['tgstat.ru', 'www.tgstat.ru'].includes(url.hostname)
  )
    return null;
  const match = /^\/channel\/@([a-z][a-z0-9_]{0,31})\/?$/i.exec(url.pathname);
  return match?.[1] ? publicUsername(match[1]) : null;
}
